// The singabot settings resource is one object edited in cards, each card
// sitting on the page where its setting is used. A card saves only its own
// keys (partial PUT). Ranges mirror the backend; the server still has the
// final word and answers 422 `invalid_settings` for the cross-field rules.
//
// `type`:  int   whole number within min..max
//          bool  true/false select
//          slot  15, 30 or 60
// `nullable`: an empty input is sent as an explicit null (clears the key).

export const FIELDS = {
  grace_days: { type: 'int', min: 1, max: 14 },
  autoclose_interval_hours: { type: 'int', min: 1, max: 24 },
  reopen_min: { type: 'int', min: 1, max: 365 },
  reopen_max: { type: 'int', min: 1, max: 365 },
  reopen_days: { type: 'int', min: 1, max: 365 },
  pause_on_tenant_wait: { type: 'bool' },
  booking_window_days: { type: 'int', min: 1, max: 90 },
  earliest_booking_days: { type: 'int', min: 1, max: 7 },
  slot_step_minutes: { type: 'slot' },
  offer_expiry_days: { type: 'int', min: 1, max: 7 },
  // Hours before an open offer closes; empty = no reminder.
  offer_reminder_hours: { type: 'int', min: 1, max: 72, nullable: true },
  reoffer_max: { type: 'int', min: 0, max: 5 },
  // A number, not only whole ones: the API returns 25.0 and takes 12.5.
  max_distance_km: { type: 'decimal', min: 1, max: 200, nullable: true },
  // Ticket defaults. The prefix is 2-6 capitals or empty (ids then start TKT).
  ticket_prefix: { type: 'prefix', nullable: true },
  max_images: { type: 'int', min: 1, max: 20 },
  max_image_mb: { type: 'int', min: 1, max: 16 },
  digest_time: { type: 'time' },
  checkback_time: { type: 'time' },
  digest_days: { type: 'days' },
  digest_warn_days: { type: 'int', min: 1, max: 30 },
};

export const PREFIX_PATTERN = /^[A-Z]{2,6}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
// Weekday numbers: 0 = Monday ... 6 = Sunday.
export const DIGEST_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

export const SLOT_STEPS = [15, 30, 60];

export const CARDS = {
  clock: {
    keys: [
      'grace_days',
      'autoclose_interval_hours',
      'reopen_min',
      'reopen_max',
      'reopen_days',
      'pause_on_tenant_wait',
    ],
  },
  booking: {
    keys: [
      'booking_window_days',
      'earliest_booking_days',
      'slot_step_minutes',
      'offer_expiry_days',
      'offer_reminder_hours',
      'reoffer_max',
      'max_distance_km',
    ],
    showTimezone: true,
  },
  tdef: {
    keys: [
      'ticket_prefix',
      'max_images',
      'max_image_mb',
      'digest_time',
      'digest_days',
      'digest_warn_days',
      'checkback_time',
    ],
  },
};

// Form values are strings (inputs) or booleans (selects). Start from the
// stored settings so an untouched card saves what it loaded.
export function cardValues(cardId, settings) {
  const values = {};
  CARDS[cardId].keys.forEach(key => {
    const stored = settings ? settings[key] : null;
    if (FIELDS[key].type === 'days') {
      values[key] = Array.isArray(stored) ? [...stored] : [];
    } else {
      values[key] = stored === null || stored === undefined ? '' : stored;
    }
  });
  return values;
}

const isWholeNumber = raw => raw !== '' && Number.isInteger(Number(raw));

// Returns { key: errorCode } for the card. Error codes map to
// CO_LIVING.SETTINGS.ERRORS.<CODE> (RANGE carries { min, max }).
export function validateCard(cardId, values) {
  const errors = {};
  CARDS[cardId].keys.forEach(key => {
    const field = FIELDS[key];
    const raw = values[key];
    if (field.type === 'bool') return;
    if (field.type === 'prefix') {
      if (raw !== '' && !PREFIX_PATTERN.test(raw)) errors[key] = 'PREFIX';
      return;
    }
    if (field.type === 'time') {
      if (!TIME_PATTERN.test(String(raw))) errors[key] = 'TIME';
      return;
    }
    if (field.type === 'days') {
      if (!Array.isArray(raw) || !raw.length) errors[key] = 'DAYS';
      return;
    }
    if (field.type === 'slot') {
      if (!SLOT_STEPS.includes(Number(raw))) errors[key] = 'SLOT';
      return;
    }
    if (raw === '' && field.nullable) return;
    if (field.type === 'decimal') {
      const value = Number(raw);
      if (
        raw === '' ||
        !Number.isFinite(value) ||
        value < field.min ||
        value > field.max
      ) {
        errors[key] = 'RANGE';
      }
      return;
    }
    if (
      !isWholeNumber(raw) ||
      Number(raw) < field.min ||
      Number(raw) > field.max
    ) {
      errors[key] = 'RANGE';
    }
  });

  const n = key => Number(values[key]);
  if (cardId === 'clock') {
    if (
      !errors.reopen_min &&
      !errors.reopen_max &&
      n('reopen_min') > n('reopen_max')
    ) {
      errors.reopen_max = 'REOPEN_ORDER';
    }
    if (
      !errors.reopen_days &&
      !errors.reopen_min &&
      !errors.reopen_max &&
      (n('reopen_days') < n('reopen_min') || n('reopen_days') > n('reopen_max'))
    ) {
      errors.reopen_days = 'REOPEN_WINDOW';
    }
  }
  if (cardId === 'booking') {
    if (
      !errors.earliest_booking_days &&
      !errors.booking_window_days &&
      n('earliest_booking_days') > n('booking_window_days')
    ) {
      errors.earliest_booking_days = 'LEAD_ORDER';
    }
  }
  return errors;
}

// Typed body for PUT /settings: numbers as numbers, an empty nullable key as
// an explicit null so the server clears it.
export function cardPayload(cardId, values) {
  const payload = {};
  CARDS[cardId].keys.forEach(key => {
    const field = FIELDS[key];
    const raw = values[key];
    if (field.type === 'bool') {
      payload[key] = raw === true || raw === 'true';
    } else if (field.type === 'prefix') {
      payload[key] = raw === '' ? null : raw;
    } else if (field.type === 'time') {
      payload[key] = raw;
    } else if (field.type === 'days') {
      payload[key] = [...new Set(raw.map(Number))].sort((a, b) => a - b);
    } else if (raw === '' && field.nullable) {
      payload[key] = null;
    } else {
      payload[key] = Number(raw);
    }
  });
  return payload;
}
